import { computeEnemyAttackTileKeys } from "#/game/selection/options";
import {
  buildUnitSelection,
  canInspectOwnUnitAttackRange,
  canOpenBuildMenu,
  isJoinTarget,
  isLoadTarget,
} from "#/game/selection/transitions";
import type { StoreState } from "#/game/store/types";

import type { TileSelectContext } from "./tileSelectContext";
import { openBuildingSelectionAtTile } from "./tileSelectSharedHelpers";

export function selectEnemyInspect(ctx: TileSelectContext): StoreState {
  const { state, local, clickedUnit } = ctx;
  if (!clickedUnit || clickedUnit.owner === local.currentPlayer) return state;

  const sel = state.selection;
  if (sel.mode === "enemy-inspect" && sel.unitId === clickedUnit.id) {
    return { ...state, selection: { mode: "none" }, previewAnimation: null };
  }
  return {
    ...state,
    selection: {
      mode: "enemy-inspect",
      unitId: clickedUnit.id,
      attackableTileKeys: computeEnemyAttackTileKeys(local, clickedUnit),
    },
    previewAnimation: null,
  };
}

export function handleNoneOrEnemyInspect(ctx: TileSelectContext): StoreState {
  const { state, local, tile, clickedUnit, clickedOwnSelectable } = ctx;
  const sel = state.selection;

  if (clickedOwnSelectable) {
    return { ...state, selection: buildUnitSelection(local, clickedOwnSelectable) };
  }

  if (clickedUnit && canInspectOwnUnitAttackRange(local, clickedUnit)) {
    if (sel.mode === "enemy-inspect" && sel.unitId === clickedUnit.id) {
      return { ...state, selection: { mode: "none" } };
    }
    return {
      ...state,
      selection: {
        mode: "enemy-inspect",
        unitId: clickedUnit.id,
        attackableTileKeys: computeEnemyAttackTileKeys(local, clickedUnit),
      },
    };
  }

  if (clickedUnit && clickedUnit.owner !== local.currentPlayer) {
    return selectEnemyInspect(ctx);
  }

  if (canOpenBuildMenu(local, tile)) {
    return openBuildingSelectionAtTile({ state, local, tile });
  }

  if (sel.mode === "enemy-inspect") {
    return { ...state, selection: { mode: "none" } };
  }

  return state;
}

export function trySelectDifferentOwnUnit(ctx: TileSelectContext): StoreState | undefined {
  const { state, local, clickedOwnSelectable } = ctx;
  const sel = state.selection;
  if (!clickedOwnSelectable) return undefined;
  if (sel.mode === "building") return undefined;
  if (sel.mode !== "unit" && sel.mode !== "move-preview") return undefined;
  if (clickedOwnSelectable.id === sel.unitId) return undefined;
  if (isJoinTarget(sel, clickedOwnSelectable.id)) return undefined;
  if (isLoadTarget(sel, clickedOwnSelectable.id)) return undefined;

  return { ...state, selection: buildUnitSelection(local, clickedOwnSelectable) };
}
